import { useEffect, useState } from 'react';
import { twJoin } from 'tailwind-merge';

import Button from 'molecules/Button';
import Image from 'molecules/Image';
import Link from 'molecules/Link';

import { hasArrayValues } from 'utils/array/arrays';
import { scrollToAnchor } from 'utils/scroll/scrollToAnchor';

import type { FC, MouseEvent } from 'react';

export type ConnectNavLink = { id: string; label: string; href: string };

export interface ConnectNavProps {
  links?: ConnectNavLink[];
  /**
   * Optional right-aligned CTA. Left unset by default: registration for Connect
   * 2026 closed when the event ended, so the nav ships without a button and only
   * grows one again if a caller passes it explicitly.
   */
  button?: { label?: string; href?: string };
  sticky?: boolean;
}

// Links are plain in-page anchors (no hardcoded path), so the nav works at
// whatever URL the landing lives at (e.g. /connect or a test slug). The section
// IDs double as the "am I on the landing?" check.
const DEFAULT_LINKS: ConnectNavLink[] = [
  { id: 'sessions', label: 'Sessions', href: '#sessions' },
  { id: 'speakers', label: 'Speakers', href: '#speakers' },
];

// The nav is shared by the landing page (which owns these sections) and the
// standalone thank-you page (which does not). We never hardcode the landing URL:
// while the nav is rendered on the landing we stash its real pathname, so that on
// other pages the anchors can resolve back to it cross-page.
const LANDING_PATH_KEY = 'connectLandingPath';

const getSectionId = (href: string) => href.split('#')[1] || '';

const ConnectNav: FC<ConnectNavProps> = ({ links = DEFAULT_LINKS, button, sticky = true }) => {
  // Path to prefix the anchors with so they resolve cross-page. Empty string on
  // the landing itself (the anchors are in-page there).
  const [basePath, setBasePath] = useState('');

  useEffect(() => {
    const sectionIds = [...links.map(link => getSectionId(link.href)), getSectionId(button?.href || '')].filter(
      Boolean,
    );
    const onLanding = sectionIds.some(id => document.getElementById(id));

    try {
      if (onLanding) {
        window.sessionStorage.setItem(LANDING_PATH_KEY, window.location.pathname);
        setBasePath('');
      } else {
        setBasePath(window.sessionStorage.getItem(LANDING_PATH_KEY) || '');
      }
    } catch {
      setBasePath('');
    }
  }, [links, button?.href]);

  // On the landing (target exists) smooth-scroll in-page; elsewhere let the
  // browser follow the resolved cross-page href. `scroll-behavior: smooth` on
  // <body> is ignored for the document scroll in most browsers, so we drive the
  // in-page case from JS; targets carry `scroll-mt-16` to clear the sticky nav.
  const handleAnchorClick = (href: string) => (event: MouseEvent<HTMLElement>) => {
    const id = getSectionId(href);
    if (id && document.getElementById(id)) {
      event.preventDefault();
      scrollToAnchor(`#${id}`);
    }
  };

  const resolveHref = (href: string) => {
    const hashIndex = href.indexOf('#');

    return hashIndex === -1 ? href : `${basePath}${href.slice(hashIndex)}`;
  };

  return (
    <nav className={twJoin('dark z-[999] w-full bg-gray-900', sticky && 'sticky top-0')}>
      <div className="mx-auto flex h-18 max-w-screen-xl items-center justify-center gap-8 px-4 sm:px-16 lg:h-20 lg:justify-end lg:px-8">
        {/* Mobile/tablet: just the centered Prismatic logo. */}
        <Link href="/" className="block w-[140px] lg:hidden">
          <Image
            url="/prismatic-logo.svg"
            width={140}
            height={29}
            alt="Prismatic logo"
            noFill
            priority
            className="h-auto w-full object-contain"
          />
        </Link>

        {/* Desktop: nav links (plus the optional CTA, if one is passed), right-aligned. */}
        <div className="hidden items-center gap-8 lg:flex">
          {hasArrayValues(links) &&
            links.map(link => (
              <Link
                key={link.id}
                href={resolveHref(link.href)}
                onClick={handleAnchorClick(link.href)}
                enablePreview={false}
                className="text-md font-medium text-white transition-colors hover:text-primary-300"
              >
                {link.label}
              </Link>
            ))}

          {button?.label && (
            <Button
              label={button.label}
              href={resolveHref(button.href || '')}
              hierarchy="primary"
              forceSmallerSize
              onClick={button.href ? handleAnchorClick(button.href) : undefined}
            />
          )}
        </div>
      </div>
    </nav>
  );
};

export default ConnectNav;
